import React from "react";
import {
  Page,
  View,
  Text,
  StyleSheet,
  Document,
  Svg,
  Path,
} from "@react-pdf/renderer";
import moment from "moment";
import PdfHeader from "../../containers/User/PortInformation/pdfHeader";
import PdfFooter from "../../containers/User/PortInformation/pdfFooter";

// --- Modern Design System Theme Palette ---
const colors = {
  bgApp: "#ffffff",
  panelBg: "#ffffff",
  headerBg: "#F1F5F9",
  border: "#cbd5e1",
  textDark: "#000000",
  tableHeadBg: "#F1F5F9",
  zebraEven: "#f8fafc",
  zebraOdd: "#ffffff",
};

const CheckboxIcon = ({ checked }) => {
  if (checked) {
    return (
      <Svg width={7} height={7} viewBox="0 0 12 12">
        <Path
          d="M2.5 6L5 8.5L9.5 3.5"
          stroke="#000000"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />
      </Svg>
    );
  }
  return null;
};

const styles = StyleSheet.create({
  page: {
    backgroundColor: colors.bgApp,
  },
  contentWrapper: {
    paddingHorizontal: 10,
    paddingVertical: 6,
    flex: 1,
  },
  // --- Layout Containers ---
  mainRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 6,
    width: "100%",
    alignItems: "stretch",
  },
  colCargo: { width: "15%", display: "flex", flexDirection: "column" },
  colVessel: { width: "35%", display: "flex", flexDirection: "column" },
  colMargin: { width: "17%", display: "flex", flexDirection: "column" },
  colFreight: { width: "17%", display: "flex", flexDirection: "column" },
  colResult: { width: "15%", display: "flex", flexDirection: "column" },

  // --- Main Panel Styles ---
  panel: {
    backgroundColor: colors.panelBg,
    borderRadius: 0,
    borderWidth: 0.5,
    borderColor: colors.border,
    overflow: "hidden",
    flexGrow: 1,
    display: "flex",
    flexDirection: "column",
  },
  panelHeader: {
    backgroundColor: colors.headerBg,
    paddingVertical: 3,
    paddingHorizontal: 4,
    borderBottomWidth: 0.5,
    borderColor: colors.border,
  },
  panelHeaderText: {
    color: "#000000",
    fontSize: 6,
    fontWeight: "bold",
  },
  panelBody: {
    padding: 0,
    flexGrow: 1,
  },

  // --- Structured Key-Value Table Grid Styles ---
  kvRow: {
    flexDirection: "row",
    borderBottomWidth: 0.5,
    borderColor: colors.border,
    minHeight: 11,
    alignItems: "stretch",
  },
  kvLabelCell: {
    width: "48%",
    backgroundColor: colors.headerBg,
    paddingVertical: 1.5,
    paddingHorizontal: 2.5,
    justifyContent: "center",
    borderRightWidth: 0.5,
    borderColor: colors.border,
  },
  kvLabelText: {
    fontSize: 6,
    color: colors.textDark,
    fontFamily: "Helvetica",
  },
  kvValueCell: {
    width: "52%",
    backgroundColor: "#ffffff",
    paddingVertical: 1.5,
    paddingHorizontal: 2.5,
    justifyContent: "center",
  },
  kvValueText: {
    fontSize: 6,
    color: colors.textDark,
    fontWeight: "bold",
    fontFamily: "Helvetica",
  },

  // --- Nested Tables (Consumptions & Fuel) ---
  consTableContainer: {
    borderWidth: 0.5,
    borderColor: colors.border,
    borderRadius: 0,
    overflow: "hidden",
    marginBottom: 3,
    backgroundColor: "#fff",
  },
  consMainHeader: {
    backgroundColor: colors.headerBg,
    paddingVertical: 2,
    alignItems: "center",
    justifyContent: "center",
    borderBottomWidth: 0.5,
    borderColor: colors.border,
  },
  consMainHeaderText: {
    color: "#000000",
    fontSize: 6,
    fontWeight: "bold",
  },
  consThRow: {
    flexDirection: "row",
    backgroundColor: colors.tableHeadBg,
    borderBottomWidth: 0.5,
    borderColor: colors.border,
  },
  consThCell: {
    flex: 1,
    fontSize: 6,
    paddingVertical: 1.5,
    paddingHorizontal: 2,
    color: colors.textDark,
    textAlign: "left",
    fontWeight: "bold",
    borderRightWidth: 0.5,
    borderColor: colors.border,
  },
  consTrRow: {
    flexDirection: "row",
    borderBottomWidth: 0.5,
    borderColor: colors.border,
    alignItems: "center",
  },
  consTdCell: {
    flex: 1,
    fontSize: 6,
    paddingVertical: 1.5,
    paddingHorizontal: 2,
    color: colors.textDark,
    textAlign: "left",
    justifyContent: "center",
    alignItems: "center",
    borderRightWidth: 0.5,
    borderColor: colors.border,
  },
  tableSectionTitle: {
    fontSize: 6,
    fontWeight: "bold",
    color: colors.textDark,
    backgroundColor: colors.headerBg,
    borderTopWidth: 0.5,
    borderBottomWidth: 0.5,
    borderColor: colors.border,
    paddingVertical: 2,
    paddingHorizontal: 3,
  },

  // --- Bottom Operations Table ---
  opsPanel: {
    marginTop: 4,
    backgroundColor: "#ffffff",
    borderWidth: 0.5,
    borderColor: colors.border,
    borderRadius: 0,
    overflow: "hidden",
  },
  opsHeaderRow: {
    flexDirection: "row",
    backgroundColor: colors.headerBg,
    borderBottomWidth: 0.5,
    borderColor: colors.border,
  },
  opsTh: {
    fontSize: 6,
    color: "#000000",
    paddingVertical: 2.5,
    paddingHorizontal: 2,
    textAlign: "left",
    fontWeight: "bold",
    borderRightWidth: 0.5,
    borderColor: colors.border,
  },
  opsTrEven: { flexDirection: "row", backgroundColor: colors.zebraEven, borderBottomWidth: 0.5, borderColor: colors.border },
  opsTrOdd: { flexDirection: "row", backgroundColor: colors.zebraOdd, borderBottomWidth: 0.5, borderColor: colors.border },
  opsTd: {
    fontSize: 6,
    color: colors.textDark,
    paddingVertical: 2,
    paddingHorizontal: 2,
    textAlign: "left",
    borderRightWidth: 0.5,
    borderColor: colors.border,
  },
});

// --- UI Sub-Components ---

const KvBox = ({ label, value, labelWidth = "48%", valueWidth = "52%" }) => (
  <View style={styles.kvRow}>
    <View style={[styles.kvLabelCell, { width: labelWidth }]}>
      <Text style={styles.kvLabelText} maxLines={1}>{label}</Text>
    </View>
    <View style={[styles.kvValueCell, { width: valueWidth }]}>
      <Text style={styles.kvValueText} maxLines={1}>
        {value !== undefined && value !== null && value !== "" ? String(value) : "-"}
      </Text>
    </View>
  </View>
);

const ConsumptionSubTable = ({ title, data, isSea }) => {
  const headers = isSea ? ["", "SPEED", "HS", "LS", "MGO"] : ["OPR", "HS", "LS", "MGO"];
  return (
    <View style={styles.consTableContainer}>
      <View style={styles.consMainHeader}>
        <Text style={styles.consMainHeaderText}>{title}</Text>
      </View>
      <View style={styles.consThRow}>
        {headers.map((h, i) => (
          <Text key={i} style={[styles.consThCell, i === headers.length - 1 && { borderRightWidth: 0 }]}>{h}</Text>
        ))}
      </View>
      {data.map((row, rowIndex) => (
        <View key={rowIndex} style={[styles.consTrRow, { backgroundColor: rowIndex % 2 === 0 ? colors.zebraOdd : colors.zebraEven }]}>
          {isSea ? (
            <View style={[styles.consTdCell, { justifyContent: "center", alignItems: "center" }]}>
              <CheckboxIcon checked={Boolean(row.Selected)} />
            </View>
          ) : (
            <Text style={styles.consTdCell}>{row.Operation}</Text>
          )}
          {isSea && <Text style={styles.consTdCell}>{row.Speed || "-"}</Text>}
          <Text style={styles.consTdCell}>{row.HS || "-"}</Text>
          <Text style={styles.consTdCell}>{row.LS || "-"}</Text>
          <Text style={[styles.consTdCell, { borderRightWidth: 0 }]}>{row.MGO || "-"}</Text>
        </View>
      ))}
    </View>
  );
};

// --- Main Render Component ---

const PdfDataComponent = ({ data }) => {
  const cargoDataEntries = [
    { label: "Account", value: data?.Cargo_Details?.account },
    { label: "Cargo", value: data?.Cargo_Details?.cargo },
    { label: "Laycan start", value: data?.Cargo_Details?.laycanStartDate ? moment(data?.Cargo_Details?.laycanStartDate).format("DD/MMM/YY") : "" },
    { label: "Laycan end", value: data?.Cargo_Details?.laycanEndDate ? moment(data?.Cargo_Details?.laycanEndDate).format("DD/MMM/YY") : "" },
    { label: "Adcom", value: data?.Cargo_Details?.adcom },
    { label: "Brokerage", value: data?.Cargo_Details?.brokerage },
    { label: "Others", value: data?.Cargo_Details?.other },
    { label: "Sf of cargo", value: data?.Cargo_Details?.sf_of_cargo },
    { label: "Cargo Min", value: data?.Cargo_Details?.Cargo_Min },
    { label: "Cargo Max", value: data?.Cargo_Details?.Cargo_Max },
    { label: "Frt Mode", value: data?.Cargo_Details?.freightMode },
    { label: "Tag1", value: data?.Cargo_Details?.Tag1 },
    { label: "Tag2", value: data?.Cargo_Details?.Tag2 },
    { label: "Tag3", value: data?.Cargo_Details?.Tag3 },
    { label: "Tag4", value: data?.Cargo_Details?.Tag4 },
  ];

  const vesselDetailsCol1Entries = [
    { label: "Vessel", value: data?.vessel?.vesselName },
    { label: "DWT (S)", value: data?.vessel?.DWT },
    { label: "Draft (S)", value: data?.vessel?.Draft },
    { label: "TPC", value: data?.vessel?.TPC },
    { label: "Grain", value: data?.vessel?.grain },
    { label: "Built", value: data?.vessel?.built },
    { label: "LOA", value: data?.vessel?.LOA },
    { label: "Beam", value: data?.vessel?.Beam },
    { label: "GRT", value: data?.vessel?.GRT },
    { label: "NRT", value: data?.vessel?.NRT },
    { label: "Light Ship", value: data?.vessel?.light_ship },
    { label: "Bale Cap", value: data?.vessel?.bale_capacity },
    { label: "Gears", value: data?.vessel?.Gears },
    { label: "Grabs", value: data?.vessel?.Grabs },
    { label: "Fuel Type", value: data?.vessel?.fuel_type },
    { label: "Scrubber", value: data?.vessel?.scrubber },
    { label: "Flag", value: data?.vessel?.Flag },
    { label: "Class", value: data?.vessel?.Class },
    { label: "IMO", value: data?.vessel?.IMO },
    { label: "MMSI", value: data?.vessel?.MMIS },
  ];

  const ballastData = [
    { Selected: data?.vessel?.BalComb1, Speed: data?.vessel?.Ballast_Sea_Consumptions?.col_1?.Speed, HS: data?.vessel?.Ballast_Sea_Consumptions?.col_1?.HS, LS: data?.vessel?.Ballast_Sea_Consumptions?.col_1?.LS, MGO: data?.vessel?.Ballast_Sea_Consumptions?.col_1?.MGO },
    { Selected: data?.vessel?.BalComb2, Speed: data?.vessel?.Ballast_Sea_Consumptions?.col_2?.Speed, HS: data?.vessel?.Ballast_Sea_Consumptions?.col_2?.HS, LS: data?.vessel?.Ballast_Sea_Consumptions?.col_2?.LS, MGO: data?.vessel?.Ballast_Sea_Consumptions?.col_2?.MGO },
    { Selected: data?.vessel?.BalComb3, Speed: data?.vessel?.Ballast_Sea_Consumptions?.col_3?.Speed, HS: data?.vessel?.Ballast_Sea_Consumptions?.col_3?.HS, LS: data?.vessel?.Ballast_Sea_Consumptions?.col_3?.LS, MGO: data?.vessel?.Ballast_Sea_Consumptions?.col_3?.MGO },
    { Selected: data?.vessel?.BalComb4, Speed: data?.vessel?.Ballast_Sea_Consumptions?.col_4?.Speed, HS: data?.vessel?.Ballast_Sea_Consumptions?.col_4?.HS, LS: data?.vessel?.Ballast_Sea_Consumptions?.col_4?.LS, MGO: data?.vessel?.Ballast_Sea_Consumptions?.col_4?.MGO },
  ];

  const ladenData = [
    { Selected: data?.vessel?.LadComb1, Speed: data?.vessel?.Laden_Sea_Consumptions?.col_1?.Speed, HS: data?.vessel?.Laden_Sea_Consumptions?.col_1?.HS, LS: data?.vessel?.Laden_Sea_Consumptions?.col_1?.LS, MGO: data?.vessel?.Laden_Sea_Consumptions?.col_1?.MGO },
    { Selected: data?.vessel?.LadComb2, Speed: data?.vessel?.Laden_Sea_Consumptions?.col_2?.Speed, HS: data?.vessel?.Laden_Sea_Consumptions?.col_2?.HS, LS: data?.vessel?.Laden_Sea_Consumptions?.col_2?.LS, MGO: data?.vessel?.Laden_Sea_Consumptions?.col_2?.MGO },
    { Selected: data?.vessel?.LadComb3, Speed: data?.vessel?.Laden_Sea_Consumptions?.col_3?.Speed, HS: data?.vessel?.Laden_Sea_Consumptions?.col_3?.HS, LS: data?.vessel?.Laden_Sea_Consumptions?.col_3?.LS, MGO: data?.vessel?.Laden_Sea_Consumptions?.col_3?.MGO },
    { Selected: data?.vessel?.LadComb4, Speed: data?.vessel?.Laden_Sea_Consumptions?.col_4?.Speed, HS: data?.vessel?.Laden_Sea_Consumptions?.col_4?.HS, LS: data?.vessel?.Laden_Sea_Consumptions?.col_4?.LS, MGO: data?.vessel?.Laden_Sea_Consumptions?.col_4?.MGO },
  ];

  const portData = [
    { Operation: "Idle", HS: data?.vessel?.Port_Consumptions?.Idle?.HS, LS: data?.vessel?.Port_Consumptions?.Idle?.LS, MGO: data?.vessel?.Port_Consumptions?.Idle?.MGO },
    { Operation: "Loading", HS: data?.vessel?.Port_Consumptions?.Loading?.HS, LS: data?.vessel?.Port_Consumptions?.Loading?.LS, MGO: data?.vessel?.Port_Consumptions?.Loading?.MGO },
    { Operation: "Disch", HS: data?.vessel?.Port_Consumptions?.Disch?.HS, LS: data?.vessel?.Port_Consumptions?.Disch?.LS, MGO: data?.vessel?.Port_Consumptions?.Disch?.MGO },
    { Operation: "Extra", HS: data?.vessel?.Port_Consumptions?.Extra?.HS, LS: data?.vessel?.Port_Consumptions?.Extra?.LS, MGO: data?.vessel?.Port_Consumptions?.Extra?.MGO },
  ];

  const marginDataEntries = [
    { label: "DWF", value: data?.Margin_and_Allowances?.DWF },
    { label: "Spd Perf Factor", value: data?.Margin_and_Allowances?.Perf_Factor },
    { label: "Speed Reduce", value: data?.Margin_and_Allowances?.speedReduce },
    { label: "Bnkr Con Allow", value: data?.Margin_and_Allowances?.bnkrFactor },
    { label: "Fuel Calc Method", value: data?.Margin_and_Allowances?.fuelCalculationMethod },
  ];

  const fuelPrices = {
    Main: data?.Margin_and_Allowances?.Fuel_Price?.Normal,
    ECA: data?.Margin_and_Allowances?.Fuel_Price?.ECA,
    MGO: data?.Margin_and_Allowances?.Fuel_Price?.MGO,
  };

  const bunkerFuelSpecs = [
    { label: "Main", value: data?.Margin_and_Allowances?.Bunker_Fuel?.Main },
    { label: "ECA", value: data?.Margin_and_Allowances?.Bunker_Fuel?.ECA },
    { label: "MDO", value: data?.Margin_and_Allowances?.Bunker_Fuel?.MGO },
  ];

  const fmt2 = (v) => (v !== undefined && v !== null && v !== "" ? Number(v).toFixed(2) : "-");

  const freightResultEntries = [
    { label: "Freight Rate", value: data?.Freight_and_Result?.Freight_Rate },
    { label: "TC Eq", value: data?.Freight_and_Result?.tcEq !== undefined && data?.Freight_and_Result?.tcEq !== null ? Number(data.Freight_and_Result.tcEq).toFixed(2) : "-" },
    { label: "TC Eq GBB", value: fmt2(data?.Freight_and_Result?.gbb) },
    { label: "Hire Per Day", value: data?.Freight_and_Result?.hirePerDay },
    { label: "Hire GBB", value: data?.Freight_and_Result?.hireGbb },
    { label: "Profit/day", value: data?.Freight_and_Result?.profitPerDay !== undefined && data?.Freight_and_Result?.profitPerDay !== null ? Number(data.Freight_and_Result.profitPerDay).toFixed(2) : "-" },
    { label: "Profit/day GBB", value: fmt2(data?.Freight_and_Result?.profitPerDayGBB) },
    { label: "Misc Revenue", value: data?.Freight_and_Result?.Misc_Revenue },
    { label: "Expenses Per Day", value: data?.Freight_and_Result?.CVE },
    { label: "On/Off Hire Survey", value: data?.Freight_and_Result?.hireSurvey },
    { label: "Canal Expenses", value: data?.Freight_and_Result?.canalExpenses },
    { label: "Additional Premium", value: data?.Freight_and_Result?.additionalPremium },
    { label: "ILOHC", value: data?.Freight_and_Result?.ilohc },
    { label: "Manoevring Exp", value: data?.Freight_and_Result?.manoevringExp },
    { label: "Misc Exp", value: data?.Freight_and_Result?.miscExp },
  ];

  const resultDetailsEntries = [
    { label: "Frt Cost", value: data?.result?.frtCost !== undefined && data?.result?.frtCost !== null ? Number(data.result.frtCost).toFixed(2) : "-" },
    { label: "Profit PMT", value: data?.result?.profitPmt !== undefined && data?.result?.profitPmt !== null ? Number(data.result.profitPmt).toFixed(2) : "-" },
    { label: "Profit Net", value: data?.result?.profitNet !== undefined && data?.result?.profitNet !== null ? Number(data.result.profitNet).toFixed(2) : "-" },
    { label: "Net Frt Rev", value: data?.result?.netFrtRevenue !== undefined && data?.result?.netFrtRevenue !== null ? Number(data.result.netFrtRevenue).toFixed(2) : "-" },
    { label: "Hire Exp", value: data?.result?.hireExpense !== undefined && data?.result?.hireExpense !== null ? Number(data.result.hireExpense).toFixed(2) : "-" },
    { label: "Fuel Exp", value: data?.result?.fuelExpense !== undefined && data?.result?.fuelExpense !== null ? Number(data.result.fuelExpense).toFixed(2) : "-" },
    { label: "Others", value: data?.result?.otherExpense !== undefined && data?.result?.otherExpense !== null ? Number(data.result.otherExpense).toFixed(2) : "-" },
    { label: "Main Fuel Qty", value: data?.result?.hsQuantity !== undefined && data?.result?.hsQuantity !== null ? Number(data.result.hsQuantity).toFixed(2) : "-" },
    { label: "ECA Qty", value: data?.result?.lsQuantity !== undefined && data?.result?.lsQuantity !== null ? Number(data.result.lsQuantity).toFixed(2) : "-" },
    { label: "MGO Qty", value: data?.result?.mgoQuantity !== undefined && data?.result?.mgoQuantity !== null ? Number(data.result.mgoQuantity).toFixed(2) : "-" },
    { label: "Port Stay", value: fmt2(data?.result?.portStayTotal) },
    { label: "Voy Duration", value: data?.result?.voyageDuration !== undefined && data?.result?.voyageDuration !== null ? Number(data.result.voyageDuration).toFixed(2) : "-" },
    { label: "Stowage", value: data?.result?.stowage !== undefined && data?.result?.stowage !== null ? Number(data.result.stowage).toFixed(2) : "-" },
    { label: "Fuel Settle Profit", value: data?.result?.fuelSettlementProfit !== undefined && data?.result?.fuelSettlementProfit !== null ? Number(data.result.fuelSettlementProfit).toFixed(2) : "-" },
  ];

  const opsData = data?.add_operations || [];


  return (
    <Document>
      <Page size="A4" orientation="landscape" style={styles.page}>
        <PdfHeader />

        <View style={styles.contentWrapper}>

          {/* --- MAIN 5-COLUMN DASHBOARD AREA --- */}
          <View style={styles.mainRow}>

            {/* COLUMN 1: CARGO DETAILS */}
            <View style={styles.colCargo}>
              <View style={styles.panel}>
                <View style={styles.panelHeader}><Text style={styles.panelHeaderText}>Cargo Details</Text></View>
                <View style={styles.panelBody}>
                  {cargoDataEntries.map((item, i) => (
                    <KvBox key={i} label={item.label} value={item.value} />
                  ))}
                </View>
              </View>
            </View>

            {/* COLUMN 2: VESSEL DETAILS & CONSUMPTION */}
            <View style={styles.colVessel}>
              <View style={styles.panel}>
                <View style={styles.panelHeader}><Text style={styles.panelHeaderText}>Vessel Details & Consumptions</Text></View>
                <View style={[styles.panelBody, { flexDirection: "row" }]}>
                  {/* Left Side: Specifics */}
                  <View style={{ width: "42%", borderRightWidth: 0.5, borderColor: colors.border }}>
                    {vesselDetailsCol1Entries.map((item, i) => (
                      <KvBox key={i} label={item.label} value={item.value} labelWidth="45%" valueWidth="55%" />
                    ))}
                  </View>
                  {/* Right Side: Consumptions */}
                  <View style={{ width: "58%", paddingLeft: 3, paddingRight: 3, paddingTop: 3 }}>
                    <ConsumptionSubTable title="Ballast Sea Consumptions" data={ballastData} isSea={true} />
                    <ConsumptionSubTable title="Laden Sea Consumptions" data={ladenData} isSea={true} />
                    <ConsumptionSubTable title="Port Consumptions" data={portData} isSea={false} />
                  </View>
                </View>
              </View>
            </View>

            {/* COLUMN 3: MARGINS & ALLOWANCES */}
            <View style={styles.colMargin}>
              <View style={styles.panel}>
                <View style={styles.panelHeader}><Text style={styles.panelHeaderText}>Margin and Allowances</Text></View>
                <View style={styles.panelBody}>
                  {marginDataEntries.map((item, i) => (
                    <KvBox key={i} label={item.label} value={item.value} />
                  ))}

                  {/* FUEL PRICE TABLE */}
                  <View style={[styles.consTableContainer, { marginTop: 3, marginBottom: 3 }]}>
                    <View style={styles.consMainHeader}>
                      <Text style={styles.consMainHeaderText}>Fuel Price</Text>
                    </View>
                    <View style={styles.consThRow}>
                      <Text style={styles.consThCell}>MAIN</Text>
                      <Text style={styles.consThCell}>ECA</Text>
                      <Text style={[styles.consThCell, { borderRightWidth: 0 }]}>MGO</Text>
                    </View>
                    <View style={[styles.consTrRow, { backgroundColor: colors.zebraOdd }]}>
                      <Text style={styles.consTdCell}>{fuelPrices.Main || "0"}</Text>
                      <Text style={styles.consTdCell}>{fuelPrices.ECA || "0"}</Text>
                      <Text style={[styles.consTdCell, { borderRightWidth: 0 }]}>{fuelPrices.MGO || "0"}</Text>
                    </View>
                  </View>

                  <Text style={styles.tableSectionTitle}>Bunker Fuel Specifications</Text>
                  {bunkerFuelSpecs.map((item, i) => (
                    <KvBox key={i} label={item.label} value={item.value} />
                  ))}

                  <Text style={styles.tableSectionTitle}>This Vessel:</Text>
                  <KvBox label="TC Adcom %" value={data?.Margin_and_Allowances?.tcAdcom} />
                  <KvBox label="Brokerage" value={data?.Margin_and_Allowances?.brokerage} />
                  <KvBox label="Extra Expenses" value={data?.Margin_and_Allowances?.extraExpense} />
                  <KvBox label="Constants" value={data?.Margin_and_Allowances?.constants} />
                </View>
              </View>
            </View>

            {/* COLUMN 4: FREIGHT & RESULT */}
            <View style={styles.colFreight}>
              <View style={styles.panel}>
                <View style={styles.panelHeader}><Text style={styles.panelHeaderText}>Freight and Result</Text></View>
                <View style={styles.panelBody}>
                  {freightResultEntries.map((item, i) => (
                    <KvBox key={i} label={item.label} value={item.value} />
                  ))}
                </View>
              </View>
            </View>

            {/* COLUMN 5: RESULT DETAILS */}
            <View style={styles.colResult}>
              <View style={styles.panel}>
                <View style={styles.panelHeader}><Text style={styles.panelHeaderText}>Result Details</Text></View>
                <View style={styles.panelBody}>
                  {resultDetailsEntries.map((item, i) => (
                    <KvBox key={i} label={item.label} value={item.value} />
                  ))}
                </View>
              </View>
            </View>

          </View>

          {/* --- BOTTOM OPERATIONS TABLE --- */}
          {opsData.length > 0 && (
            <View style={styles.opsPanel}>
              <View style={styles.opsHeaderRow}>
                <Text style={[styles.opsTh, { flex: 0.6 }]}>B/L</Text>
                <Text style={[styles.opsTh, { flex: 1.5 }]}>Operation</Text>
                <Text style={[styles.opsTh, { flex: 2.5 }]}>Port</Text>
                <Text style={[styles.opsTh, { flex: 1.5 }]}>Dist Nor</Text>
                <Text style={[styles.opsTh, { flex: 1.5 }]}>Dist ECA</Text>
                <Text style={[styles.opsTh, { flex: 1 }]}>DWF</Text>
                <Text style={[styles.opsTh, { flex: 1 }]}>Spd N</Text>
                <Text style={[styles.opsTh, { flex: 1 }]}>Spd E</Text>
                <Text style={[styles.opsTh, { flex: 1.5 }]}>Cargo</Text>
                <Text style={[styles.opsTh, { flex: 1.5 }]}>Rate</Text>
                <Text style={[styles.opsTh, { flex: 1.5 }]}>Terms</Text>
                <Text style={[styles.opsTh, { flex: 1 }]}>Fac</Text>
                <Text style={[styles.opsTh, { flex: 1 }]}>Turn</Text>
                <Text style={[styles.opsTh, { flex: 1 }]}>Ext</Text>
                <Text style={[styles.opsTh, { flex: 1 }]}>P.Exp</Text>
                <Text style={[styles.opsTh, { flex: 1.5 }]}>P.Stay</Text>
                <Text style={[styles.opsTh, { flex: 1.5 }]}>Emiss</Text>
                <Text style={[styles.opsTh, { flex: 1.5 }]}>Loadline</Text>
                <Text style={[styles.opsTh, { flex: 1 }]}>Draft</Text>
                <Text style={[styles.opsTh, { flex: 1 }]}>W.Den</Text>
                <Text style={[styles.opsTh, { flex: 1, borderRightWidth: 0 }]}>Supply</Text>
              </View>
              {opsData.map((op, i) => (
                <View key={i} style={i % 2 === 0 ? styles.opsTrEven : styles.opsTrOdd}>
                  <Text style={[styles.opsTd, { flex: 0.6 }]}>{op?.lbmarker === "ballast" ? "B" : op?.lbmarker === "laden" ? "L" : "-"}</Text>
                  <Text style={[styles.opsTd, { flex: 1.5 }]}>{op?.operation || "-"}</Text>
                  <Text style={[styles.opsTd, { flex: 2.5 }]}>{op?.Port || "-"}</Text>
                  <Text style={[styles.opsTd, { flex: 1.5 }]}>{op?.distNormal !== undefined ? op.distNormal : "0"}</Text>
                  <Text style={[styles.opsTd, { flex: 1.5 }]}>{op?.distEca !== undefined ? op.distEca : "0"}</Text>
                  <Text style={[styles.opsTd, { flex: 1 }]}>{op?.dwf !== undefined ? op.dwf : "0"}</Text>
                  <Text style={[styles.opsTd, { flex: 1 }]}>{op?.speedNormal !== undefined ? op.speedNormal : "0"}</Text>
                  <Text style={[styles.opsTd, { flex: 1 }]}>{op?.speedECA !== undefined ? op.speedECA : "0"}</Text>
                  <Text style={[styles.opsTd, { flex: 1.5 }]}>{op?.cargoTx !== undefined ? op.cargoTx : "0"}</Text>
                  <Text style={[styles.opsTd, { flex: 1.5 }]}>{op?.rate !== undefined ? op.rate : "0"}</Text>
                  <Text style={[styles.opsTd, { flex: 1.5 }]}>{op?.terms || "-"}</Text>
                  <Text style={[styles.opsTd, { flex: 1 }]}>{op?.factor !== undefined ? op.factor : "0"}</Text>
                  <Text style={[styles.opsTd, { flex: 1 }]}>{op?.turnTime !== undefined ? op.turnTime : "0"}</Text>
                  <Text style={[styles.opsTd, { flex: 1 }]}>{op?.extraDays !== undefined ? op.extraDays : "0"}</Text>
                  <Text style={[styles.opsTd, { flex: 1 }]}>{op?.portExp !== undefined ? op.portExp : "0"}</Text>
                  <Text style={[styles.opsTd, { flex: 1.5 }]}>{op?.portStay !== undefined && op.portStay !== null ? Number(op.portStay).toFixed(2) : "0"}</Text>
                  <Text style={[styles.opsTd, { flex: 1.5 }]}>{op?.Port_emission || "-"}</Text>
                  <Text style={[styles.opsTd, { flex: 1.5 }]}>{op?.loadline || "-"}</Text>
                  <Text style={[styles.opsTd, { flex: 1 }]}>{op?.draftRes ?? "0"}</Text>
                  <Text style={[styles.opsTd, { flex: 1 }]}>{op?.waterDensity ?? "0"}</Text>
                  <Text style={[styles.opsTd, { flex: 1, borderRightWidth: 0 }]}>{op?.supply ?? "0"}</Text>
                </View>
              ))}
            </View>
          )}

        </View>

        <PdfFooter />
      </Page>
    </Document>
  );
};

export default PdfDataComponent;